<template>
  <div class="game-board">
    <canvas ref="canvas"
      tabIndex="0"
      :autofocus="autofocus"
      class="game-canvas"
      @mousedown.prevent
      @mousemove="onInput"
      @mousewheel="onInput"
      @mouseout="onInput"
      @click.prevent="onInput"
      @contextmenu.prevent="onInput"
      @touchstart.prevent="onInput"
      @touchmove.prevent="onInput"
      @touchend.prevent="onInput"
      @focusout="onCanvasFocusOut"
      />
    <Renderer
        :canvas="canvas"
        :playMode="playMode"
        :state="state"
        :inputState="inputState"
        @uiSettings="onUISettings"
        />
    <!-- Marks drawn over the board (plan 2026-10-29-sealed-moves.md): the sealed stone its sealer
         sees, and the pulse of a sealed stone just opened. -->
    <template v-if="px">
      <div v-for="m in marks" :key="m.key" class="mark" :class="m.kind" :style="markStyle(m)">
        <svg v-if="m.kind === 'sealed'" viewBox="0 0 100 100"><circle cx="50" cy="50" r="47"/><text x="50" y="52">{{ m.text }}</text></svg>
      </div>
    </template>
  </div>
</template>

<script>
import State, { StoneChain, MarkChain, InfluenceChain, InputValidator } from './State';
import InputState from './InputState';
import Renderer from './Renderer';
import InputHandler, { Action } from './InputHandler';

export { State, StoneChain, MarkChain, InfluenceChain, InputValidator, Action };

export default {
  name: 'GameBoard',

  components: {
    Renderer,
  },

  props: {
    size: { type: Number, default: 700 },
    playMode: { type: String, default: null },
    state: { type: State, required: true },
    // Takes the keyboard when the page opens; off for a preview below other content, which the
    // browser would otherwise scroll to.
    autofocus: { type: Boolean, default: true },
    // [{ key, kind: 'sealed' | 'pulse', x, y, text }] drawn over the board.
    marks: { type: Array, default: () => [] },
  },

  watch: {
    'state.input.inputType': function () {
      this.inputState.reset();
    },

    playMode() {
      this.inputState.reset();
    },
  },

  data() {
    return {
      canvas: null,
      context: null,
      inputHandler: null,
      inputState: new InputState(),
      // The board's geometry in canvas pixels ({ start, spacing, stone }), once drawn.
      px: null,
    };
  },

  async mounted() {
    this.canvas = this.$refs['canvas'];
    this.context = this.canvas.getContext('2d');
    document.addEventListener('pointerdown', this.onDocumentPointerDown, true);

    if(this.size > 0) {
      this.canvas.width = this.size;
      this.canvas.height = this.size;
    } else {
      this.canvas.width = this.canvas.parentElement.clientWidth - 2;
      this.canvas.height = this.canvas.width;
    }
  },

  beforeDestroy() { document.removeEventListener('pointerdown', this.onDocumentPointerDown, true); },

  methods: {
    onInput(e) {
      if(!this.inputHandler) return;

      if(e.type === 'click' || e.type == 'touchstart')
        this.canvas.focus();

      this.inputHandler.handle(e, a => {
        switch(a.type) {
          case Action.Type.CURSOR:
            if(this.state.input.inputType !== null)
              this.inputState.updateCursor(a.point);
            break;

          case Action.Type.PUT:
            if(this.state.input.inputType === null) {
              // No point (nothing is placed), but where the tap was: the replay opens a suggestion's line there.
              const put = new Action(Action.Type.PUT);
              put.at = a.point;
              this.$emit('action', put);
              break;
            }

            this.inputState.updateCursor(null);

            if(this.playMode === 'button') {
              if(this.inputState.updateSelected(a.point))
                this.$emit('action', new Action(Action.Type.SELECT, a.point));
            } else {
              this.$emit('action', a);
            }
            break;

          default:
            this.$emit('action', a);
        }
      });
    },

    clearSelection() {
      if(this.inputState.updateSelected(null))
        this.$emit('action', new Action(Action.Type.SELECT, null));
    },

    onDocumentPointerDown(e) {
      // A tap outside is explicit intent to leave the selection, even when that element cannot
      // take focus. A native permission dialog or iframe focus change is not an outside tap. 取消封手
      // clears it itself: clearing it at the press would move the button away before the click.
      if(!this.$el.contains(e.target) && !e.target.closest('.put, .placement, .seal-cancel'))
        this.clearSelection();
    },

    onCanvasFocusOut(e) {
      // Android WebViews and native WeChat controls can omit relatedTarget. Clearing here would
      // unmount the placement button before its click/subscription callback can play the stone.
      // Known keyboard focus destinations still clear selection; outside taps are handled above.
      if(e.relatedTarget && !e.relatedTarget.closest('.put, .placement, .seal-cancel'))
        this.clearSelection();
    },

    onUISettings(ui) {
      this.inputHandler = new InputHandler(this.state.grid, ui.px.start, ui.px.spacing);
      this.px = { start: ui.px.start, spacing: ui.px.spacing, stone: ui.px.spacing * ui.board.stoneSize };
    },

    // A mark centred on its point, a stone wide (the canvas has a 1 px border; the board's box may be
    // wider than the canvas).
    markStyle(m) {
      const d = this.px.stone, scale = this.canvas ? this.canvas.clientWidth / this.canvas.width || 1 : 1;
      const c = this.canvas || { offsetLeft: 0, offsetTop: 0 };
      const x = c.offsetLeft + 1 + (this.px.start + this.px.spacing * m.x) * scale, y = c.offsetTop + 1 + (this.px.start + this.px.spacing * m.y) * scale;
      return { left: `${x - d * scale / 2}px`, top: `${y - d * scale / 2}px`, width: `${d * scale}px`, height: `${d * scale}px` };
    },
  },
}
</script>

<style scoped>
.game-board { position: relative; }

.mark {
  position: absolute;
  pointer-events: none;
  box-sizing: border-box;
}

.mark svg { display: block; width: 100%; height: 100%; }
.mark.sealed circle { fill: rgba(0, 0, 0, .55); stroke: #000; stroke-width: 3; }
.mark.sealed text {
  fill: #fff;
  font-size: 58px;
  font-weight: bold;
  text-anchor: middle;
  dominant-baseline: central;
}

/* A ring grows from the stone's edge and fades, three times. */
.mark.pulse {
  border-radius: 50%;
  border: 3px solid #0175ff;
  opacity: 0;
  animation: seal-pulse 1s ease-out 3;
}

@keyframes seal-pulse {
  from { transform: scale(1); opacity: .9; }
  to { transform: scale(1.9); opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .mark.pulse { animation: seal-pulse-still 3s linear 1; }
  @keyframes seal-pulse-still { from, 90% { opacity: .9; transform: scale(1.3); } to { opacity: 0; } }
}

.game-canvas {
  border: solid 1px #aaa;
  outline: none;
}
</style>
